/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import cx from 'classnames';
import React, { useEffect, useRef } from 'react';
import styles from './styles.module.scss';
import { View } from '@az/starc-ui';
import { useModalOPPromptContext } from '@/components/ModalOPPromptContext';
import { SideTab } from './SideTab/SideTab';
import { OptIn } from './OptIn/OptIn';
import dynamic from 'next/dynamic';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { useLocale } from '@/hooks/useLocale';

const LazyOptInModal = dynamic(
  () => import('./OptInModal/OptInModal').then(({ OptInModal }) => OptInModal),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

export function EmailSideTab() {
  const { promoState, handleTogglePromo, shouldHideComponent } = useModalOPPromptContext();
  const emailSideTab = useRef<HTMLDivElement>(null);
  const locale = useLocale();
  const isUS = locale === 'en-US';
  const showModal = isUS;

  useEffect(() => {
    if (showModal) {
      return;
    }

    function handleClickOutside(e: MouseEvent) {
      if (
        emailSideTab.current &&
        e.target instanceof Node &&
        !emailSideTab.current.contains(e.target) &&
        promoState.isOpen
      ) {
        const isVerticalScrollbarClick = isClickOnVerticalScrollbar(e);
        if (!isVerticalScrollbarClick) {
          handleTogglePromo();
        }
      }
    }

    function handleScroll(e: Event) {
      if (promoState.isOpen) {
        const target = e.target as Node;
        const windowWidth = window.innerWidth;

        if (windowWidth < 2420) {
          if (emailSideTab.current && !emailSideTab.current.contains(target)) {
            handleTogglePromo();
          }
        }
      }
    }

    function isClickOnVerticalScrollbar(e: MouseEvent): boolean {
      const windowWidth = document.documentElement.clientWidth;
      const documentWidth = document.documentElement.scrollWidth;
      if (documentWidth > windowWidth && e.clientX > windowWidth - 20) {
        return true;
      }
      return false;
    }
    if (promoState.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [handleTogglePromo, promoState.isOpen, showModal]);

  if (shouldHideComponent) {
    return null;
  }

  return (
    <>
      <View
        className={cx(styles.emailSideTab, {
          [styles.expanded]: promoState.isOpen,
          [styles.disappear]: promoState.isModalOpened,
        })}
        attributes={{ ref: emailSideTab }}
      >
        <SideTab />
        <OptIn />
      </View>
      {showModal ? <LazyOptInModal /> : null}
    </>
  );
}
